// ccp/src/lib/callState.js
//
// D37 (2026-09-16): the pure half of the call lifecycle the rails and the
// dialer key on. Kept free of React and Streams so
// scripts/verify-call-controls.mjs can prove it without a browser.
//
// Why this exists. An agent-initiated outbound call is "dialling" from the
// moment Streams fires contact.onConnecting until the far end answers
// (onConnected). Before D37 the provider recorded a contact ONLY at
// onConnected: onConnecting for an outbound contact deliberately did nothing
// (no accept screen is wanted), so while the customer's phone rang
// state.contact was null, callActive was false, the End control was disabled
// and handleEnd would have refused with the D7 "no active contact" warning.
// There was no way to drop an unanswered outbound call from the console.
//
// The fix is a real dialling state, not a wider gate: the contact is held from
// onConnecting, End is enabled (Mute/Hold are not — there is no media yet),
// the dial gate treats dialling as busy, and onEnded/onMissed/onDestroy clear it.

export const CALL_FIELDS = Object.freeze({
  contact: null,
  callActive: false,   // a CONNECTED contact (media or task) — timers, mute, hold
  dialling: false,     // an outbound contact we placed, ringing, not yet answered
  activeChannel: null, // 'voice' | 'task' | 'chat' | null
  isMuted: false,
  isHeld: false,
});

// Streams: contact.isInbound() is false for agent-initiated outbound calls.
// Treat anything that cannot answer as inbound (the accept screen is the safe
// default for an unknown contact; a missing dialling state is the D37 bug).
export function isOutboundContact(contact) {
  try { return typeof contact?.isInbound === 'function' && contact.isInbound() === false; }
  catch { return false; }
}

// The lifecycle transitions for the CALL_FIELDS slice. Returns the changed
// fields for the three actions it owns, or null for anything else.
export function callTransition(state, action) {
  switch (action?.type) {
    case 'CONTACT_DIALLING':
      // An answered call never regresses to dialling; a second outbound
      // contact cannot replace a connected one.
      if (state.callActive) return null;
      return { contact: action.contact, dialling: true, callActive: false,
               activeChannel: action.channel ?? 'voice', isMuted: false, isHeld: false };
    case 'CONTACT_CONNECTED':
      return { contact: action.contact, dialling: false, callActive: true,
               activeChannel: action.channel ?? null, isMuted: false, isHeld: false };
    case 'CONTACT_ENDED':
      return { contact: null, dialling: false, callActive: false,
               activeChannel: null, isMuted: false, isHeld: false };
    default:
      return null;
  }
}

// Which rail controls are usable. End is the only control that works on a
// ringing outbound call; there is nothing to mute or hold until it connects.
export function controlsFor({ callActive = false, dialling = false } = {}) {
  return { mute: !!callActive, hold: !!callActive, end: !!(callActive || dialling) };
}

// The dial gate. Returns the reason a new outbound dial must be refused, or
// null when dialling is allowed. A ringing outbound call is busy — before D37
// the gate saw callActive=false while ringing and let a second dial through.
export function dialBlockedReason({ callActive = false, dialling = false, activeChannel = null, dialPending = false } = {}) {
  if (dialPending) return 'Finish the current call before starting another.';
  if (dialling) return 'Finish the current call before starting another.';
  if (callActive && activeChannel === 'voice') return 'Finish the current call before starting another.';
  return null;
}

// Row 94 (2026-10-06, Storm: "you should not be able to wrap up if the call is
// still live, you need to end the call or chat first"). On 6 Oct a callback
// task ended while the agent's outbound call was still ringing; the console put
// the wrap-up form over the live call, and saving stored the notes but could not
// complete the contact. A wrap-up is now held back while a voice call is
// connected or ringing, or a chat is connected. A connected TASK does not hold
// it back: wrapping up the call while the callback task is still open is the
// normal callback sequence. Returns the reason to show, or null.
export function wrapUpBlockedReason({ callActive = false, dialling = false, activeChannel = null } = {}) {
  if ((callActive || dialling) && activeChannel === 'voice') return 'End the call before wrapping up.';
  if (callActive && activeChannel === 'chat') return 'End the chat before wrapping up.';
  return null;
}

// Left-rail status pill text.
export function statusLabel({ callActive = false, dialling = false, contactType = null, agentStatus = null } = {}) {
  if (callActive) return contactType === 'task' ? 'Active Task' : 'Active Call';
  if (dialling) return 'Dialling…';
  return agentStatus ?? 'Available';
}

// ── D39 (2026-09-16): our own hang-up is not a softphone fault ──────────────
//
// Streams models a contact that leaves `connecting` without reaching
// `connected` as the `error` state, and its contact event graph fires BOTH
// MISSED (connecting → error) and ERROR (ANY → error) for that one transition.
// So cancelling a ringing outbound call — which D37 made possible for the
// first time — necessarily raises contact.onError, and the 2026-09-14 incident
// handler reported every ERROR as a fault. Connect itself recorded those calls
// as a clean AGENT_DISCONNECT with nothing left stuck.
//
// The fix is NOT to stop reporting ERROR. It is to know which ERRORs are the
// direct consequence of a hang-up we just performed. Anything else still
// banners, including a contact that errors on its own (the 14 Sep case).

export const INTENTIONAL_END_WINDOW_MS = 5000;   // narrow: the ERROR follows the destroy within ~1 s

// Record that we deliberately ended this contact. `map` is a plain Map kept in
// a ref. Entries older than the window are pruned on every write so it cannot
// grow across a shift.
export function markIntentionalEnd(map, contactId, now = Date.now(), windowMs = INTENTIONAL_END_WINDOW_MS) {
  if (!map || !contactId) return map;
  for (const [id, at] of map) if (now - at > windowMs) map.delete(id);
  map.set(contactId, now);
  return map;
}

// Did we end this contact ourselves, just now? Consumes the entry: a second
// ERROR for the same contact still banners, so a genuine fault after our
// hang-up is not swallowed.
export function wasIntentionalEnd(map, contactId, now = Date.now(), windowMs = INTENTIONAL_END_WINDOW_MS) {
  if (!map || !contactId) return false;
  const at = map.get(contactId);
  if (at == null) return false;
  map.delete(contactId);
  return now - at <= windowMs;
}

// ── Ring outcome (2026-09-23, ring-out banner) ───────────────────────────
// An inbound contact that rings out unanswered leaves `connecting` for
// `error` exactly like a cancelled outbound leg does (D39): Streams fires
// MISSED and ERROR off that one transition. That contact was missed, not
// faulted, and the console must say so. Detection is deliberately narrow:
// the contact was announced as an inbound ring, no Accept was attempted, and
// it rang for at least RING_TIMEOUT_MIN_MS before the ERROR. Connect offers a
// voice contact for ~20 s (the 2026-09-23 ring-out: offered 09:09:47, MISSED
// 09:10:07; the 20 s figure is AWS's, not configurable per re:Post — an
// assumption, not repo-verified). An ERROR earlier than that — a failed
// connection, the caller hanging up mid-ring — still banners exactly as
// before. There is no channel gate here; in practice only VOICE reaches it,
// because in Streams' event graph (2.25.1) a task or chat that rings out goes
// connecting -> missed and a Decline goes -> rejected, neither of which fires
// ERROR. A media fault while ringing surfaces through agent.onSoftphoneError,
// a separate path this decision never touches.
export const RING_TIMEOUT_MIN_MS = 15000;
// Ring entries are consumed by classifyContactError (or cleared on connect);
// anything older than this is a ring that never resolved through onError.
export const RING_ENTRY_MAX_AGE_MS = 10 * 60 * 1000;

export function markRingStart(map, contactId, now = Date.now(), maxAgeMs = RING_ENTRY_MAX_AGE_MS) {
  if (!map || !contactId) return map;
  for (const [id, at] of map) if (now - at > maxAgeMs) map.delete(id);
  map.set(contactId, now);
  return map;
}

export function ringDurationMs(map, contactId, now = Date.now()) {
  const at = map?.get(contactId);
  return at == null ? null : now - at;
}

export function clearRingStart(map, contactId) {
  if (map && contactId) map.delete(contactId);
}

// The one decision contact.onError makes:
//   'cancelled' — we ended this contact ourselves just now (D39)
//   'missed'    — an inbound ring that timed out unanswered (this fix)
//   'fault'     — everything else: banner, as before
// Consumes the ring entry and the accept-attempt entry for the contact, so a
// second ERROR for the same contact still banners (same rule as D39).
export function classifyContactError({ intentionalEnds, ringStarts, acceptAttempts, contactId, now = Date.now(), minRingMs = RING_TIMEOUT_MIN_MS } = {}) {
  if (!contactId) return 'fault';
  // Read and consume every entry for this contact first, whatever the verdict,
  // so a second ERROR for the same contact always lands on 'fault'.
  const ring = ringDurationMs(ringStarts, contactId, now);
  clearRingStart(ringStarts, contactId);
  const accepted = Boolean(acceptAttempts?.has(contactId));
  acceptAttempts?.delete(contactId);
  if (wasIntentionalEnd(intentionalEnds, contactId, now)) return 'cancelled';
  if (ring != null && ring >= minRingMs && !accepted) return 'missed';
  return 'fault';
}

// F7 (dress rehearsal 2026-10-15; row 36, fourth sighting, contact 48ee5439):
// Streams can call accept()'s failure callback while Connect goes on to connect
// the contact a few seconds later (seen 3 s later on a queued callback). The
// red "refused the Accept" banner then sat over a live call. The console now
// waits ACCEPT_FAIL_GRACE_MS and decides from the contact's status then:
//   'late_success' — it connected: no banner, a quiet diagnostic record only
//   'fault'        — anything else (still ringing, ended, unreadable): banner
// 16 Oct proof call (contact e690f193): on a queued callback Connect plays the recording
// notice to the customer before bridging the agent (~10 s), so the Accept can resolve
// 10 s after the failure callback. 4 s was too short; 15 s covers it with margin.
export const ACCEPT_FAIL_GRACE_MS = 15000;
export function acceptFailureVerdict(statusType) {
  return statusType === 'connected' ? 'late_success' : 'fault';
}

// What the console knows about an errored contact. Until now the ERROR record
// carried only an id prefix, which is why the first real occurrence could not
// be diagnosed from the record alone. Every read is defensive: Streams throws
// from these getters on a half-built contact (D21).
export function contactErrorDetail(contact) {
  const out = { contactId: null, status: null, stateDurationMs: null, type: null };
  try { out.contactId = contact?.getContactId?.() ?? null; } catch { /* unavailable */ }
  try { out.status = contact?.getStatus?.()?.type ?? null; } catch { /* unavailable */ }
  try { const d = contact?.getStateDuration?.(); out.stateDurationMs = Number.isFinite(d) ? d : null; } catch { /* unavailable */ }
  try { out.type = String(contact?.getType?.() ?? '').toLowerCase() || null; } catch { /* unavailable */ }
  return out;
}

// The one-line detail the banner and the stored record show.
export function formatErrorDetail(detail) {
  if (!detail) return '';
  const bits = [];
  if (detail.contactId) bits.push(`contact ${String(detail.contactId).slice(0, 8)}…`);
  if (detail.status) bits.push(`state ${detail.status}`);
  if (detail.type) bits.push(detail.type);
  if (Number.isFinite(detail.stateDurationMs)) bits.push(`${Math.round(detail.stateDurationMs / 1000)}s in state`);
  return bits.join(' · ');
}
